import React, { useState } from "react";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaCalendarCheck, 
  FaWhatsapp,
  FaPhoneAlt,
  FaEnvelope,
  FaArrowRight,
  FaHeart,
  FaStar,
  FaShieldAlt,
  FaBolt
} from "react-icons/fa";
import api from "../api";

// Refined Elegant Theme
const THEME = {
  maroon: "#6b1331",
  rose: "#b23d5a",
  gold: "#d4a574",
  cream: "#faf8f5",
  blush: "#fdf2f6",
  dark: "#2d2d2d",
  text: "#4a4a4a",
  lightText: "#777",
  white: "#ffffff"
};

const WHATSAPP_NUMBER = "+918920514929";
const PHONE_NUMBER_1 = "+918439616352";
const PHONE_NUMBER_2 = "+918920514929";
const OWNER_NAME = "Director Amandeep Chaudhary";

function Contact() {
  const [form, setForm] = useState({ 
    name: "", 
    email: "", 
    phone: "",
    eventType: "",
    eventDate: "",
    amount: 1999,
    message: ""
  });
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeMethod, setActiveMethod] = useState("form"); // "form" | "whatsapp" | "call"

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const openWhatsApp = () => {
    const text = `Hello ${OWNER_NAME}, I'm interested in booking an event with Parivar.%0A%0A` +
      `*Name:* ${form.name || "Not provided"}%0A` +
      `*Email:* ${form.email || "Not provided"}%0A` +
      `*Phone:* ${form.phone || "Not provided"}%0A` +
      `*Event Type:* ${form.eventType || "Not specified"}%0A` +
      `*Event Date:* ${form.eventDate || "Not decided"}%0A` +
      `*Budget:* ₹${form.amount}%0A` +
      `*Message:* ${form.message || "No additional message"}`;
    
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.post("/payment/order", { amount: form.amount });
      const orderData = res.data;
      setOrder(orderData);

      const RZP_KEY =
        process.env.REACT_APP_RAZORPAY_KEY_ID ||
        process.env.REACT_APP_RAZORPAY_KEY;
      if (!RZP_KEY) {
        throw new Error("Razorpay key is not configured");
      }

      const options = {
        key: RZP_KEY,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Parivar",
        description: "Booking Payment",
        order_id: orderData.id,
        handler: async function (response) {
          try {
            const { data: verify } = await api.post("/payment/verify-payment", response);
            if (!verify?.verified) {
              alert("Verification failed. Contact support.");
              return;
            }

            await api.post("/testbooking", {
              uid: response.razorpay_payment_id,
              bookingData: {
                name: form.name,
                email: form.email,
                phone: form.phone,
                eventType: form.eventType,
                eventDate: form.eventDate,
                amount: form.amount,
                message: form.message,
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              },
            });
            
            const confirmText = `Hello ${form.name}, your payment of ₹${form.amount} has been received! 🎉%0A` +
              `Order ID: ${response.razorpay_order_id}%0A` +
              `We'll contact you shortly. - ${OWNER_NAME}, Parivar`;
            window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${confirmText}`, "_blank");
            
            alert("Payment successful and booking saved!");
          } catch (dbErr) {
            alert("Payment was successful, but saving booking failed!");
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone
        },
        theme: { color: "#b0354c" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError("Failed to create payment order. " + err.message);
    }
    setSubmitting(false);
  };

  const eventTypes = [
    "Wedding",
    "Engagement", 
    "Sangeet",
    "Reception",
    "Birthday Party",
    "Corporate Event",
    "Other"
  ];

  const features = [
    { icon: <FaShieldAlt />, text: "Secure Payment" },
    { icon: <FaBolt />, text: "Instant Confirm" },
    { icon: <FaHeart />, text: "Personalized Service" }
  ];

  return (
    <section
      id="contact"
      style={{
        background: `linear-gradient(135deg, ${THEME.cream} 0%, ${THEME.blush} 100%)`,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        padding: "40px 20px"
      }}
    >
      {/* Main Container - Bento Grid Layout */}
      <div
        style={{
          maxWidth: 1200,
          width: "100%",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gap: 24,
          alignItems: "start"
        }}
      >
        {/* LEFT COLUMN - Info & Features (5 cols) */}
        <div
          style={{
            gridColumn: "span 12",
            display: "flex",
            flexDirection: "column",
            gap: 20
          }}
        >
          {/* Hero Card */}
          <div
            style={{
              background: THEME.white,
              borderRadius: 24,
              padding: "32px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 120,
                height: 120,
                background: `linear-gradient(135deg, ${THEME.blush} 0%, transparent 100%)`,
                borderRadius: "0 0 0 100%"
              }}
            />
            
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${THEME.rose} 0%, ${THEME.maroon} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                  boxShadow: "0 8px 24px rgba(107, 19, 49, 0.25)"
                }}
              >
                <FaCalendarCheck size={24} color="#fff" />
              </div>
              
              <h1
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: THEME.maroon,
                  marginBottom: 12,
                  lineHeight: 1.2
                }}
              >
                Book Your Dream<br />
                <span style={{ color: THEME.rose }}>Wedding Event</span>
              </h1>
              
              <p style={{ 
                color: THEME.lightText, 
                fontSize: "1rem",
                lineHeight: 1.6,
                margin: 0
              }}>
                Let us make your special day unforgettable. Choose how you'd like to connect with us.
              </p>
            </div>
          </div>

          {/* Method Selector Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12
            }}
          >
            {[
              { id: "form", icon: <FaCalendarCheck />, label: "Book Online", color: THEME.maroon },
              { id: "whatsapp", icon: <FaWhatsapp />, label: "WhatsApp", color: "#25D366" },
              { id: "call", icon: <FaPhoneAlt />, label: "Call Us", color: THEME.rose }
            ].map((method) => (
              <button
                key={method.id}
                onClick={() => setActiveMethod(method.id)}
                style={{
                  background: activeMethod === method.id ? method.color : THEME.white,
                  color: activeMethod === method.id ? "#fff" : THEME.text,
                  border: "none",
                  borderRadius: 16,
                  padding: "16px 12px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: activeMethod === method.id 
                    ? `0 8px 24px ${method.color}40` 
                    : "0 2px 12px rgba(0,0,0,0.04)"
                }}
              >
                <span style={{ fontSize: "1.5rem" }}>{method.icon}</span>
                <span style={{ 
                  fontSize: "0.8rem", 
                  fontWeight: 600,
                  whiteSpace: "nowrap"
                }}>
                  {method.label}
                </span>
              </button>
            ))}
          </div>

          {/* Features Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12
            }}
          >
            {features.map((feature, idx) => (
              <div
                key={idx}
                style={{
                  background: THEME.white,
                  borderRadius: 16,
                  padding: "20px 16px",
                  textAlign: "center",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)"
                }}
              >
                <div style={{ 
                  color: THEME.rose, 
                  fontSize: "1.25rem",
                  marginBottom: 8
                }}>
                  {feature.icon}
                </div>
                <div style={{ 
                  fontSize: "0.75rem", 
                  fontWeight: 600,
                  color: THEME.text
                }}>
                  {feature.text}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Info */}
          <div
            style={{
              background: `linear-gradient(135deg, ${THEME.maroon} 0%, ${THEME.rose} 100%)`,
              borderRadius: 20,
              padding: "24px",
              color: "#fff"
            }}
          >
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 12,
              marginBottom: 12
            }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <FaPhoneAlt size={16} />
              </div>
              <div>
                <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>Call or WhatsApp</div>
                <div style={{ fontSize: "1rem", fontWeight: 700 }}>{PHONE_NUMBER_1}</div>
                <div style={{ fontSize: "1rem", fontWeight: 700 }}>{PHONE_NUMBER_2}</div>
              </div>
            </div>
            <div style={{ fontSize: "0.85rem", opacity: 0.9 }}>
              <FaStar style={{ marginRight: 6, color: THEME.gold }} />
              {OWNER_NAME} • Usually responds in 10 minutes
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Dynamic Form (7 cols) */}
        <div
          style={{
            gridColumn: "span 12"
          }}
        >
          <div
            style={{
              background: THEME.white,
              borderRadius: 24,
              padding: "32px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              minHeight: 500
            }}
          >
            {activeMethod === "form" && (
              <>
                <div style={{ marginBottom: 24 }}>
                  <h2
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "1.5rem",
                      color: THEME.maroon,
                      marginBottom: 8,
                      fontWeight: 700
                    }}
                  >
                    Event Details
                  </h2>
                  <p style={{ 
                    color: THEME.lightText, 
                    fontSize: "0.9rem",
                    margin: 0
                  }}>
                    Fill in your details and we'll get back to you shortly.
                  </p>
                </div>

                <Form onSubmit={handleSubmit}>
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(2, 1fr)", 
                    gap: 16 
                  }}>
                    <Form.Group className="mb-3" style={{ gridColumn: "span 2" }}>
                      <Form.Label style={labelStyle}>Full Name *</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        style={inputStyle}
                        onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, inputStyle)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Email *</Form.Label>
                      <Form.Control
                        required
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        style={inputStyle}
                        onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, inputStyle)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        style={inputStyle}
                        onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, inputStyle)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Event Type</Form.Label>
                      <Form.Select
                        name="eventType"
                        value={form.eventType}
                        onChange={handleChange}
                        style={selectStyle}
                        onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, selectStyle)}
                      >
                        <option value="">Select type</option>
                        {eventTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Event Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="eventDate"
                        value={form.eventDate}
                        onChange={handleChange}
                        style={inputStyle}
                        onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, inputStyle)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" style={{ gridColumn: "span 2" }}>
                      <Form.Label style={labelStyle}>Budget (INR) *</Form.Label>
                      <div style={{ position: "relative" }}>
                        <span style={{
                          position: "absolute",
                          left: 16,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: THEME.rose,
                          fontWeight: 700
                        }}>₹</span>
                        <Form.Control
                          required
                          type="number"
                          name="amount"
                          value={form.amount}
                          onChange={handleChange}
                          min="1000"
                          style={{
                            ...inputStyle,
                            paddingLeft: 36,
                            fontWeight: 600,
                            color: THEME.maroon
                          }}
                          onFocus={(e) => Object.assign(e.target.style, {...inputFocusStyle, paddingLeft: 36})}
                          onBlur={(e) => Object.assign(e.target.style, {...inputStyle, paddingLeft: 36})}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-4" style={{ gridColumn: "span 2" }}>
                      <Form.Label style={labelStyle}>Special Requirements</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us about your vision, preferences, or any special requests..."
                        style={{
                          ...inputStyle,
                          resize: "vertical"
                        }}
                        onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, inputStyle)}
                      />
                    </Form.Group>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    style={{
                      width: "100%",
                      background: `linear-gradient(135deg, ${THEME.rose} 0%, ${THEME.maroon} 100%)`,
                      border: "none",
                      borderRadius: 14,
                      padding: "16px 24px",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      boxShadow: "0 8px 24px rgba(107, 19, 49, 0.25)",
                      transition: "all 0.3s ease",
                      cursor: submitting ? "not-allowed" : "pointer",
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Proceed to Payment</span>
                        <FaArrowRight />
                      </>
                    )}
                  </Button>

                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    gap: 6,
                    marginTop: 16,
                    fontSize: "0.8rem",
                    color: THEME.lightText
                  }}>
                    <FaShieldAlt />
                    <span>Secured by Razorpay • Instant confirmation</span>
                  </div>
                </Form>
              </>
            )}

            {activeMethod === "whatsapp" && (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                    boxShadow: "0 8px 32px rgba(37, 211, 102, 0.3)"
                  }}
                >
                  <FaWhatsapp size={40} color="#fff" />
                </div>
                
                <h2
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "1.75rem",
                    color: THEME.maroon,
                    marginBottom: 12
                  }}
                >
                  Chat on WhatsApp
                </h2>
                
                <p style={{ 
                  color: THEME.lightText, 
                  marginBottom: 32,
                  maxWidth: 300,
                  margin: "0 auto 32px"
                }}>
                  Get instant reply from {OWNER_NAME}. Usually responds within 10 minutes.
                </p>

                <div style={{ maxWidth: 400, margin: "0 auto", textAlign: "left" }}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Your Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      style={inputStyle}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Event Type</Form.Label>
                    <Form.Select
                      name="eventType"
                      value={form.eventType}
                      onChange={handleChange}
                      style={selectStyle}
                    >
                      <option value="">Select event type</option>
                      {eventTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Hi, I'm interested in booking a wedding venue..."
                      style={inputStyle}
                    />
                  </Form.Group>

                  <Button
                    onClick={openWhatsApp}
                    style={{
                      width: "100%",
                      background: "#25D366",
                      border: "none",
                      borderRadius: 14,
                      padding: "16px 24px",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      boxShadow: "0 8px 24px rgba(37, 211, 102, 0.3)"
                    }}
                  >
                    <FaWhatsapp size={20} />
                    Start WhatsApp Chat
                  </Button>
                </div>
              </div>
            )}

            {activeMethod === "call" && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${THEME.rose} 0%, ${THEME.maroon} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                    boxShadow: `0 8px 32px ${THEME.rose}40`
                  }}
                >
                  <FaPhoneAlt size={32} color="#fff" />
                </div>
                
                <h2
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "1.75rem",
                    color: THEME.maroon,
                    marginBottom: 12
                  }}
                >
                  Call Us Directly
                </h2>
                
                <p style={{ 
                  color: THEME.lightText, 
                  marginBottom: 32
                }}>
                  Speak directly with {OWNER_NAME} for personalized assistance.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
                  <a
                    href={`tel:${PHONE_NUMBER_1}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 12,
                      background: THEME.maroon,
                      color: "#fff",
                      textDecoration: "none",
                      padding: "16px 32px",
                      borderRadius: 16,
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      boxShadow: "0 8px 24px rgba(107, 19, 49, 0.25)"
                    }}
                  >
                    <FaPhoneAlt />
                    {PHONE_NUMBER_1}
                  </a>

                  <a
                    href={`tel:${PHONE_NUMBER_2}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 12,
                      background: THEME.rose,
                      color: "#fff",
                      textDecoration: "none",
                      padding: "16px 32px",
                      borderRadius: 16,
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      boxShadow: "0 8px 24px rgba(178, 61, 90, 0.25)"
                    }}
                  >
                    <FaPhoneAlt />
                    {PHONE_NUMBER_2}
                  </a>
                </div>

                <p style={{ 
                  marginTop: 24, 
                  fontSize: "0.9rem", 
                  color: THEME.lightText 
                }}>
                  Available 9 AM - 9 PM IST
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (min-width: 768px) {
          #contact > div {
            grid-template-columns: repeat(12, 1fr) !important;
          }
          #contact > div > div:first-child {
            grid-column: span 5 !important;
            position: sticky;
            top: 40px;
          }
          #contact > div > div:last-child {
            grid-column: span 7 !important;
          }
        }
        
        @media (max-width: 767px) {
          #contact {
            padding: 20px 16px !important;
          }
          #contact > div {
            gap: 16px !important;
          }
        }
      `}</style>
    </section>
  );
}

// Shared Styles
const labelStyle = {
  fontWeight: 600,
  color: THEME.maroon,
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 6
};

const inputStyle = {
  borderRadius: 12,
  border: "2px solid #e8e8e8",
  fontSize: "0.95rem",
  padding: "12px 16px",
  transition: "all 0.2s ease",
  width: "100%"
};

const inputFocusStyle = {
  borderColor: THEME.rose,
  boxShadow: "0 0 0 3px rgba(178, 61, 90, 0.1)",
  outline: "none"
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
  background: "#fff"
};

export default Contact;